import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const LOGO_URL = `${process.env.PUBLIC_URL}/logo.webp`;

const NAV_LINKS = [
  { label: "Features", href: "/#features" },
  { label: "Modes", href: "/#modes" },
  { label: "About", href: "/#about" },
  { label: "Accessibility", href: "/#accessibility" },
  { label: "Partners", href: "/partners" },
  { label: "Resources", href: "/resources" },
  { label: "Blog", href: "/blog" },
  { label: "Videos", href: "/videos" },
  { label: "Podcast", href: "/podcast" },
  { label: "FAQ", href: "/faq" },
  { label: "Privacy", href: "/privacy" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavClick = (e, href) => {
    if (href.startsWith("/#")) {
      e.preventDefault();
      setMobileOpen(false);
      const id = href.slice(2);
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => {
          document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      setMobileOpen(false);
    }
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || mobileOpen
          ? "bg-[#F8F9FB]/95 backdrop-blur-md shadow-[0_1px_12px_rgba(0,0,0,0.04)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link
            to="/"
            data-testid="navbar-logo"
            className="flex items-center gap-2.5"
          >
            <img
              src={LOGO_URL}
              alt=""
              className="w-8 h-8 rounded-lg"
              loading="eager"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <span className="font-heading text-xl md:text-2xl font-bold text-brand-teal tracking-tight">
              NeuroNest
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map((link) =>
              link.href.startsWith("/#") ? (
                <a
                  key={link.href}
                  href={link.href}
                  data-testid={`nav-link-${link.label.toLowerCase()}`}
                  className="text-[#5A6376] hover:text-brand-teal transition-colors duration-300 text-sm font-medium"
                  onClick={(e) => handleNavClick(e, link.href)}
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`nav-link-${link.label.toLowerCase()}`}
                  className="text-[#5A6376] hover:text-brand-teal transition-colors duration-300 text-sm font-medium"
                >
                  {link.label}
                </Link>
              )
            )}
          </div>

          <Button
            data-testid="navbar-cta"
            className="hidden md:inline-flex rounded-full bg-brand-teal hover:bg-brand-teal/90 text-white px-6 py-2 text-sm font-semibold shadow-md shadow-brand-teal/20 transition-all duration-300 hover:-translate-y-0.5"
            asChild
          >
            <a href="https://play.google.com/store/apps/details?id=com.neuronest.send&pcampaignid=web_share" target="_blank" rel="noopener noreferrer">Download App</a>
          </Button>

          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2 text-[#5A6376] hover:text-brand-teal transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {mobileOpen && (
          <div
            data-testid="mobile-menu"
            className="md:hidden pb-6 pt-2 space-y-3 animate-fade-in"
          >
            {NAV_LINKS.map((link) =>
              link.href.startsWith("/#") ? (
                <a
                  key={link.href}
                  href={link.href}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                  className="block text-[#5A6376] hover:text-brand-teal py-2 font-medium"
                  onClick={(e) => handleNavClick(e, link.href)}
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                  className="block text-[#5A6376] hover:text-brand-teal py-2 font-medium"
                  onClick={() => setMobileOpen(false)}
                >
                  {link.label}
                </Link>
              )
            )}
            <Button
              data-testid="mobile-cta"
              className="rounded-full bg-brand-teal hover:bg-brand-teal/90 text-white w-full mt-3"
              asChild
            >
              <a href="https://play.google.com/store/apps/details?id=com.neuronest.send&pcampaignid=web_share" target="_blank" rel="noopener noreferrer" onClick={() => setMobileOpen(false)}>
                Download App
              </a>
            </Button>
          </div>
        )}
      </div>
    </nav>
  );
}
